/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useIntersectionObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';
import { getImageSize } from '../../lib/utils';

import { useSite } from '../../contexts/SiteContext';
import IntersectingVideo from '../blocks/employee/IntersectingVideo';
// import Image from '../partials/Image';
import RoundedButton from '../buttons/Rounded';
import Html from '../partials/Html';
import Link from '../partials/Link';

import styles from '../../../styles/cards/job-listing.module.scss';

const propTypes = {
    item: AppPropTypes.card.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const JobListingCard = ({ item, className }) => {
    const {
        title = null,
        locations = [],
        handle = null,
        published = false,
        publishedAt = null,
        description = null,
        image = null,
        video = null,
        url = null,
        sections = [],
        // backgroundColors: itemGradient = null,
    } = item || {};
    const { handle: siteHandle = null } = useSite();

    // TODO: make this happen
    // console.log('grad', itemGradient);

    const { url: videoUrl = null } = video || {};

    const imageSize = getImageSize(image, 'medium');
    const { url: imageUrl = null } = imageSize || {};

    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-15%' });

    const [buttonColor, setButtonColor] = useState('#51FE89');

    // Special case w/ less info. Don't forget to be spontanteous :)
    const isSpontaneous = handle === 'spontaneous';

    // Pick background color(s) for card + button from section(s)
    const setBackgroundColors = (colorArr) => {
        if (colorArr === null || colorArr.length === 0) {
            return { backgroundColor: '#51FE89' };
        }
        if (colorArr.length === 1) {
            setButtonColor(colorArr[0]);
            return { backgroundColor: `${colorArr[0]}` };
        }
        const colorStops = colorArr.map((color) => `${color}`);
        setButtonColor(colorStops[colorStops.length - 1]);
        return { backgroundImage: `linear-gradient(to right, ${colorStops.join(', ')})` };
    };

    const backgroundColors = useMemo(() => {
        if (isSpontaneous) {
            return setBackgroundColors(['#FF81C5', '#FF0053']);
        }
        const colorsArr = sections.reduce((highlights, section) => {
            if (section.theme && section.theme.highlights) {
                highlights.push(section.theme.highlights);
            }
            return highlights;
        }, []);
        return setBackgroundColors(colorsArr);
    }, [sections]);

    const finalLocations = useMemo(() => {
        if (locations === null || locations.length === 0) {
            return null;
        }
        return (locations || []).map(
            ({ label = null }, i) =>
                `${label}${i !== locations.length - 1 && locations.length > 1 ? ' | ' : ''}`,
        );
    }, [locations]);

    // const textColor = useMemo(
    //     () => (backgroundColor !== null && tinycolor(backgroundColor).isDark() ? '#FFF' : '#222'),
    //     [backgroundColor],
    // );
    // console.log('handle', handle)

    return (
        <div
            ref={ref}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            style={{
                opacity: `${isIntersecting ? 1 : 0}`,
                // color: elementsColor,
            }}
        >
            <div className={styles.inner} style={{ ...backgroundColors }}>
                <div className={styles.content}>
                    {published === true && publishedAt !== null && !isSpontaneous ? (
                        <small className={styles.published}>
                            <FormattedMessage
                                defaultMessage="Published on"
                                description="Published date"
                            />
                            &nbsp;
                            <FormattedDate
                                value={publishedAt}
                                day="numeric"
                                month="long"
                                year="numeric"
                            />
                        </small>
                    ) : null}
                    {title !== null ? (
                        <Html tag="h3" body={title} className={styles.title} />
                    ) : null}
                    {locations !== null && locations.length > 0 && !isSpontaneous ? (
                        <p className={styles.location}>{finalLocations}</p>
                    ) : null}
                    {description !== null ? (
                        <Html className={styles.description} body={description} />
                    ) : null}
                    {/* @TODO: Hardcoded links. Refactor when France is scoped seperately */}
                    {isSpontaneous ? (
                        <div
                            className={classNames([
                                styles.buttonsRow,
                                { [styles.reverse]: siteHandle === 'france' },
                            ])}
                        >
                            <RoundedButton
                                backgroundColor="#fff"
                                external
                                className={classNames([
                                    styles.button,
                                    styles.spontaneousButton,
                                    styles.quebec,
                                ])}
                                href="https://urbania.bamboohr.com/careers/48?source=aWQ9MTY%3D"
                            >
                                <FormattedMessage
                                    defaultMessage="Quebec"
                                    description="Button label"
                                />
                            </RoundedButton>
                            <RoundedButton
                                backgroundColor="#fff"
                                external
                                className={classNames([
                                    styles.button,
                                    styles.spontaneousButton,
                                    styles.france,
                                ])}
                                href="https://urbania.bamboohr.com/careers/49?source=aWQ9MTY%3D"
                            >
                                <FormattedMessage
                                    defaultMessage="France"
                                    description="Button label"
                                />
                            </RoundedButton>
                        </div>
                    ) : null}
                </div>

                <div
                    className={styles.imageContainer}
                    style={{ backgroundImage: imageUrl !== null ? `url(${imageUrl})` : null }}
                >
                    {videoUrl !== null ? (
                        <IntersectingVideo
                            src={videoUrl}
                            muted
                            loop
                            autoPlay
                            className={styles.video}
                        />
                    ) : null}
                    {!isSpontaneous && url !== null ? (
                        <Link href={url} external className={styles.imageLink}>
                            <div
                                className={styles.imageLabel}
                                style={{ backgroundColor: buttonColor }}
                            >
                                <FormattedMessage
                                    defaultMessage="Apply"
                                    description="Button label"
                                />
                            </div>
                        </Link>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

JobListingCard.propTypes = propTypes;
JobListingCard.defaultProps = defaultProps;

export default JobListingCard;
